import React, {
  useContext,
  useCallback,
  useState,
  createRef,
  useEffect,
} from 'react';
import { debounce } from 'lodash';
import SearchIcon from 'mdi-react/MagnifyIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import { TextInput } from '@prisjakt/ui-inputs';
import RouteContext from '../../../../routes/context';
import * as S from './styles';
import {
  ProductCollectionFilter,
  ProductCollectionNameSearchFilter,
} from '../../../../types';
import { trackProductCollectionFilterUpdate } from '../../tracking';

const debouncedDispatch = debounce(
  (query: string, dispatch: (params: any) => void, filters: any) => {
    dispatch({
      type: 'FILTER_NAME_SEARCH_UPDATE',
      query,
    });
    trackProductCollectionFilterUpdate(filters, '_product_name_search', query);
  },
  800
);

export const getQueryFromFilters = (filters?: ProductCollectionFilter[]) => {
  const nameSearchFilter = filters?.find(
    (f: any): f is ProductCollectionNameSearchFilter =>
      'type' in f && f.type === 'name-search'
  );
  return nameSearchFilter ? nameSearchFilter.query : '';
};

export const useSearchQuery = () => {
  const { getState } = useContext(RouteContext);
  const { filters } = getState();
  const [query, setQuery] = useState(getQueryFromFilters(filters));
  useEffect(() => setQuery(getQueryFromFilters(filters)), [setQuery, filters]);
  return [query, setQuery] as const;
};

type SearchInputProps = {
  showDivider?: 'before' | 'after';
  alwaysOpen?: boolean;
  inputWidth?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
};

const SearchInput = ({
  showDivider,
  alwaysOpen,
  inputWidth,
  inputRef: ref,
}: SearchInputProps) => {
  const { t } = useContext(AppContext);
  const { dispatch, getState } = useContext(RouteContext);
  const [query, setQuery] = useSearchQuery();
  const hasQuery = query.length > 0;
  const [focused, setFocused] = useState(false);
  const isFocused = focused || alwaysOpen;
  const onFocus = useCallback(() => setFocused(true), [setFocused]);
  const onBlur = useCallback(() => setFocused(false), [setFocused]);
  const inputRef: React.RefObject<HTMLInputElement> = ref ?? createRef();
  const onOpen = useCallback(() => inputRef.current?.focus(), [inputRef]);
  const onUpdate = useCallback(
    (value: string) => {
      setQuery(value);
      if (value.length === 0 || value.length > 2) {
        debouncedDispatch(value, dispatch, getState().filters);
      }
    },
    [dispatch, getState, setQuery]
  );
  const onInputChange = useCallback(e => onUpdate(e.target.value), [onUpdate]);
  const onClear = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
    onUpdate('');
  }, [onUpdate, inputRef]);
  return (
    <S.SearchInput>
      {showDivider === 'before' && (
        <S.DisplayOptionsDivider hidden={isFocused || hasQuery} />
      )}
      <S.SearchTextWrapper
        inputWidth={inputWidth}
        expanded={isFocused || hasQuery}
      >
        <TextInput
          name="query"
          variant="rounded"
          value={query}
          inputRef={inputRef}
          placeholder={t('category', 'product-search.placeholder')}
          onChange={onInputChange}
          onBlur={onBlur}
          onFocus={onFocus}
          className="search-input-text"
        />
      </S.SearchTextWrapper>
      {!isFocused && !hasQuery && (
        <S.SearchInputIcon positionAbsolute onClick={onOpen}>
          <S.Tooltip
            placement="left"
            title={t('category', 'product-search.placeholder')}
          >
            <SearchIcon />
          </S.Tooltip>
        </S.SearchInputIcon>
      )}
      {hasQuery && (
        <S.SearchInputIcon positionAbsolute onClick={onClear}>
          <CloseIcon size="16" />
        </S.SearchInputIcon>
      )}
      {showDivider === 'after' && (
        <S.DisplayOptionsDivider hidden={isFocused || hasQuery} />
      )}
    </S.SearchInput>
  );
};

export default SearchInput;
